import React, { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import styled from 'styled-components';

import { IGroupOverview, useStaffUsers } from 'core/hooks';
import { Colors } from 'services/ColorService';

import { ICreateTaskDraft, ITaskSubject, PRIORITIES } from './taskBuckets';
import { TaskSubjectPicker } from './TaskSubjectPicker';

export interface ITaskCreateSeed {
  /** Deadline column «+»: seed the due date. */
  dueDate?: string;
  /** Status column «+»: seed the status the task is created in. */
  status?: string;
  /** Preselect a group (rail selection / column context). */
  groupId?: string;
}

interface ITaskCreateModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (draft: ICreateTaskDraft) => Promise<boolean>;
  adding: boolean;
  /** Fixed group scope (group tab) — hides the subject picker. */
  groupId?: string;
  /** Fixed student scope (student tab) — hides the subject picker. */
  studentId?: string;
  /** Fixed teacher scope (teacher tab) — hides the subject picker. */
  teacherId?: string;
  /** Group list for the dashboard's create picker. */
  groups?: IGroupOverview[];
  /** Default group when none is seeded (e.g. the command center's rail). */
  defaultGroupId?: string;
  seed?: ITaskCreateSeed;
}

/**
 * Create-task dialog: all fields laid out vertically in an antd Modal (mirrors
 * TaskFromMessageModal). Opened by the board's «＋ Добавить задачу» button or a
 * per-column «＋», which seeds the due date (deadline grouping) or status
 * (status grouping). Reuses the group / roster / assignee logic of the old
 * inline create form.
 */
export const TaskCreateModal = memo(
  ({
    open,
    onClose,
    onCreate,
    adding,
    groupId,
    studentId,
    teacherId,
    groups,
    defaultGroupId,
    seed,
  }: ITaskCreateModalProps) => {
    const { t } = useTranslation();
    // On a scoped board (group / student / teacher tab) the subject is fixed by
    // the host page, so the picker is hidden entirely.
    const scoped = Boolean(groupId || studentId || teacherId);

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [subject, setSubject] = useState<ITaskSubject>({ kind: 'none' });
    const [formDue, setFormDue] = useState('');
    const [priority, setPriority] = useState('normal');
    const [formAssigneeId, setFormAssigneeId] = useState('');
    const [seedStatus, setSeedStatus] = useState<string | undefined>(undefined);

    // Reset the form and apply the seed on every open.
    useEffect(() => {
      if (!open) return;
      setTitle('');
      setDescription('');
      setFormAssigneeId('');
      setPriority('normal');
      setFormDue(seed?.dueDate || '');
      setSeedStatus(seed?.status);
      const seededGroup = seed?.groupId || defaultGroupId || '';
      setSubject(
        seededGroup ? { kind: 'group', id: seededGroup } : { kind: 'none' },
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const { staffUsers } = useStaffUsers(false);

    // A subject-less personal to-do is valid (spec 0014), but a HALF-picked
    // subject is not: the server rejects it and the modal would otherwise just
    // sit there doing nothing on OK. Require the entity once a kind is chosen.
    const subjectReady = subject.kind === 'none' || Boolean(subject.id);
    const canCreate = Boolean(title.trim()) && (scoped || subjectReady);

    const onOk = useCallback(async () => {
      if (!canCreate || adding) return;
      // On a scoped board the host page fixes the subject; otherwise take the
      // operator's pick from the subject picker.
      let scopedSubject: ITaskSubject = { kind: 'group', id: groupId };
      if (teacherId) scopedSubject = { kind: 'teacher', id: teacherId };
      else if (studentId) scopedSubject = { kind: 'student', id: studentId };
      const ok = await onCreate({
        title: title.trim(),
        description: description.trim() || undefined,
        subject: scoped ? scopedSubject : subject,
        assigneeId: formAssigneeId || undefined,
        dueDate: formDue || undefined,
        priority,
        status: seedStatus,
      });
      if (ok) onClose();
    }, [
      canCreate,
      adding,
      onCreate,
      title,
      description,
      scoped,
      subject,
      groupId,
      studentId,
      teacherId,
      formAssigneeId,
      formDue,
      priority,
      seedStatus,
      onClose,
    ]);

    return (
      <Modal
        visible={open}
        title={t('pages.tasks.createModalTitle')}
        onOk={onOk}
        onCancel={onClose}
        confirmLoading={adding}
        okText={t('pages.tasks.add')}
        cancelText={t('button.cancel')}
        okButtonProps={{ disabled: !canCreate }}
        destroyOnClose
      >
        <Field>
          <Label>{t('pages.communications.taskTitleLabel')}</Label>
          <TextInput
            autoFocus
            value={title}
            maxLength={255}
            placeholder={t('pages.dashboard.taskTitlePlaceholder')}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>

        <Field>
          <Label>{t('pages.communications.taskDescriptionLabel')}</Label>
          <TextArea
            rows={4}
            value={description}
            placeholder={t('pages.tasks.descriptionPlaceholder')}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        {!scoped ? (
          <TaskSubjectPicker
            value={subject}
            onChange={setSubject}
            groupLabel={
              groups?.find(
                (g) =>
                  g.groupId === subject.groupId || g.groupId === subject.id,
              )?.name
            }
            disabled={adding}
          />
        ) : null}

        <Row>
          <Field>
            <Label>{t('pages.tasks.drawer.due')}</Label>
            <TextInput
              type="date"
              value={formDue}
              onChange={(e) => setFormDue(e.target.value)}
            />
          </Field>
          <Field>
            <Label>{t('pages.communications.taskPriorityLabel')}</Label>
            <SelectInput
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`pages.tasks.priority.${p}`)}
                </option>
              ))}
            </SelectInput>
          </Field>
        </Row>

        <Field>
          <Label>{t('pages.tasks.assignee')}</Label>
          <SelectInput
            value={formAssigneeId}
            onChange={(e) => setFormAssigneeId(e.target.value)}
          >
            <option value="">{t('pages.tasks.assigneeMe')}</option>
            {staffUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}
              </option>
            ))}
          </SelectInput>
        </Field>
      </Modal>
    );
  },
);

TaskCreateModal.displayName = 'TaskCreateModal';

const Field = styled.div`
  flex: 1;
  min-width: 0;
  margin-bottom: 12px;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${Colors.Gray};
  margin-bottom: 4px;
`;

const TextInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
  resize: vertical;
`;

const SelectInput = styled.select`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px;
  font: inherit;
  font-size: 13px;
  background: ${Colors.White};
`;
